import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { ResumeCitizenAttendancesIndicatorM2Data } from "@/services/esus/indicator/emulti/m2/getResumeCitizensAttendancesIndicatorM2";
import {
	DetailedCitizenAttendancesIndicatorM2,
	getDetailedCitizenAttendancesIndicatorM2,
} from "@/services/esus/indicator/emulti/m2/getDetailedCitizenAttendancesIndicatorM2";

import { IndicatorEmultiM2CitizensFilters } from "./IndicatorEmultiM2CitizensFilters";
import { IndicatorEmultiM2CitizensTable } from "./IndicatorEmultiM2CitizensTable";
import { IndicatorEmultiM2CitizenAttendancesHistory } from "./IndicatorEmultiM2CitizenHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEmultiM2CitizensProps = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizens: Paginated<ResumeCitizenAttendancesIndicatorM2Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeCitizensAttendancesIndicatorM2: () => Promise<
		ResumeCitizenAttendancesIndicatorM2Data[]
	>;
};

export function IndicatorEmultiM2Citizens({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizens,
	pageNumber,
	setPageNumber,
	handleGetAllResumeCitizensAttendancesIndicatorM2,
}: Readonly<IndicatorEmultiM2CitizensProps>) {
	const { uf, ibgeCode } = useCurrentAccount();
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenAttendances, setCitizenAttendances] =
		useState<DetailedCitizenAttendancesIndicatorM2>(
			new DetailedCitizenAttendancesIndicatorM2()
		);
	const [isLoadingCitizenWithAttendances, setIsLoadingCitizenWithAttendances] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(citizenId: number) {
		setIsLoadingCitizenWithAttendances(true);
		setCitizenAttendances(
			await getDetailedCitizenAttendancesIndicatorM2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				citizenId,
			})
		);
		setIsLoadingCitizenWithAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllResumeCitizensAttendancesIndicatorM2();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorM2AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Cidadãos acompanhados - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorM2AttendancesToGenericObjectArray(
		citizensWithAttendances: ResumeCitizenAttendancesIndicatorM2Data[]
	) {
		return citizensWithAttendances.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithAttendances.birthdate
						? getAge(citizenWithAttendances.birthdate)
						: "",
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					Equipe: citizenWithAttendances.healthTeamName ?? "",
					"Equipe Ine": citizenWithAttendances.healthTeamIne ?? "",
					Area: citizenWithAttendances.area ?? "FA",
					"Ações compartilhadas": citizenWithAttendances.totalSharedAttendances,
					"Ações não compartilhadas": citizenWithAttendances.totalNonSharedAttendances,
					"Total de ações": citizenWithAttendances.totalAttendances,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAttendances || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEmultiM2CitizensFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEmultiM2CitizensTable
							citizensWithAttendances={paginatedCitizens.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizens?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizens.pageSize}
								totalPages={paginatedCitizens.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEmultiM2CitizenAttendancesHistory
							citizenAttendances={citizenAttendances}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
